import React, { useState, useEffect } from 'react';
import PRODUCT_DB_MAP, { getProductTypeDbStatus, getDbStatus, resolveDbConfig } from '../config/productDatabaseMap';

const PAGE_SIZE = 20;

const ListClients = ({ clientConfig, onLaunchApp }) => {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('clients'); // 'clients' | 'products'

  useEffect(() => {
    fetchClients();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch('/v1/products?role=all', { signal: AbortSignal.timeout(5000) });
      if (res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const data = await res.json();
          const backendProducts = data.products || [];
          // Merge with demo fallback to ensure all product types are visible
          const demoFallback = getDemoProducts();
          const seenTypes = new Set(backendProducts.map(p => p.product_type));
          const merged = [...backendProducts, ...demoFallback.filter(p => !seenTypes.has(p.product_type))];
          setProducts(merged);
          return;
        }
      }
    } catch { /* products endpoint not available */ }
    // Fallback: use demo products if backend not reachable
    setProducts(getDemoProducts());
  };

  const getDemoProducts = () => [
    { product_name: 'Restaurant Intelligence.Type-1', product_type: 'Restaurant.Type-1', industry: 'Food & Hospitality', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 2, version: 'v1' },
    { product_name: 'Gym Intelligence.Type-1', product_type: 'Gym.Type-1', industry: 'Lifestyle', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 2, version: 'v1' },
    { product_name: 'BankRisk Intelligence.Type-1', product_type: 'Financial-Bank-Type1', industry: 'Financial Services', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Financial-Bank Intelligence.Type-1', product_type: 'Financial-Bank.Type-1', industry: 'Financial Services', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 0, version: 'v1' },
    { product_name: 'Grocery Intelligence.Type-1', product_type: 'Grocery.Type-1', industry: 'Retail & Commerce', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Supermarket Intelligence.Type-1', product_type: 'Supermarket.Type-1', industry: 'Retail & Commerce', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 0, version: 'v1' },
    { product_name: 'HVAC Intelligence.Type-1', product_type: 'HVAC.Type-1', industry: 'Home Services', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Lawn Intelligence.Type-1', product_type: 'Lawn.Type-1', industry: 'Home Services', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Plumbing Intelligence.Type-1', product_type: 'Plumbing.Type-1', industry: 'Home Services', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 2, version: 'v1' },
    { product_name: 'Insurance Intelligence.Type-1', product_type: 'Insurance.Type-1', industry: 'Professional Services', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 3, version: 'v1' },
    { product_name: 'Medical Intelligence.Type-1', product_type: 'Medical.Type-1', industry: 'Healthcare', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Logistics Intelligence.Type-1', product_type: 'Logistics.Type-1', industry: 'Mobility', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Valet Intelligence.Type-1', product_type: 'Valet.Type-1', industry: 'Mobility', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Salon Intelligence.Type-1', product_type: 'Salon.Type-1', industry: 'Lifestyle', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'RealEstate Intelligence.Type-1', product_type: 'RealEstate.Type-1', industry: 'Professional Services', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 1, version: 'v1' },
    { product_name: 'Education Intelligence.Type-1', product_type: 'Education.Type-1', industry: 'Education', builder_name: 'Empirical-AiS', builder_role: 'platform_operator', subscriber_count: 0, version: 'v1' },
  ];

  const fetchClients = async () => {
    setLoading(true);
    const demo = getDemoClients().map(c => ({ ...c, _source: 'demo' }));
    let localClients = [];
    try {
      localClients = JSON.parse(localStorage.getItem('pnp_onboarded_clients') || '[]').map(c => ({ ...c, _source: 'local' }));
    } catch { /* ignore */ }

    // Backend client fetch disabled — using only hardcoded DB Ready clients
    // DynamoDB clients will be cleaned up separately
    let backendClients = [];
    let backendOk = false;

    // Subscriber session — used to repair stale local entries missing product_type
    let subscriberSession = null;
    try {
      const raw = localStorage.getItem('pnp_subscriber_session');
      if (raw) subscriberSession = JSON.parse(raw);
    } catch { /* ignore */ }

    // Build a session map keyed by client_id for multi-client repair
    // Also collect all sessions stored under pnp_all_sessions (future) or fall back to single session
    const sessionMap = {};
    if (subscriberSession?.client_id) {
      sessionMap[subscriberSession.client_id] = subscriberSession;
    }
    try {
      const allSessions = JSON.parse(localStorage.getItem('pnp_all_sessions') || '[]');
      for (const s of allSessions) {
        if (s.client_id) sessionMap[s.client_id] = s;
      }
    } catch { /* ignore */ }

    // Merge: backend > local > demo (dedup by client_id, enrich missing fields from demo)
    const demoMap = {};
    for (const d of demo) { demoMap[d.client_id] = d; }
    const seenIds = new Set();
    const merged = [];
    let localRepaired = false;
    for (const c of [...backendClients, ...localClients, ...demo]) {
      if (!seenIds.has(c.client_id)) {
        seenIds.add(c.client_id);

        // Normalize tenant_type casing (signup saves lowercase 'standalone'/'independent')
        if (c.tenant_type) {
          const tt = c.tenant_type.toLowerCase();
          if (tt === 'standalone' || tt === 'independent') c.tenant_type = 'INDEPENDENT';
          else if (tt === 'sub_client' || tt === 'subclient') c.tenant_type = 'SUB_CLIENT';
          else if (tt === 'parent') c.tenant_type = 'PARENT';
        }

        // Fill in missing fields from demo data only if backend didn't provide them
        const demoFallback = demoMap[c.client_id];
        if (demoFallback) {
          if (!c.product_type && demoFallback.product_type) c.product_type = demoFallback.product_type;
          if (!c.location && demoFallback.location) c.location = demoFallback.location;
          if (!c.app_model && demoFallback.app_model) c.app_model = demoFallback.app_model;
          if (!c.product_name && demoFallback.product_name) c.product_name = demoFallback.product_name;
        }

        // Repair stale local entries using session map (covers all signed-up clients)
        if (c._source === 'local') {
          const session = sessionMap[c.client_id];
          if (session) {
            if (!c.product_type && session.product_type) {
              c.product_type = session.product_type;
              c.product_name = c.product_name || session.product_name || '';
              c.tenant_role = c.tenant_role || session.tenant_role || 'subscriber';
              localRepaired = true;
            }
            if (!c.location && session.location) {
              c.location = session.location;
              localRepaired = true;
            }
          }
        }

        merged.push(c);
      }
    }

    // Persist ALL repaired local entries back to localStorage so they survive restarts
    try {
      const localEntries = merged
        .filter(c => c._source === 'local')
        .map(({ _source, ...rest }) => rest);
      // Always write back to keep localStorage in sync with any normalization applied
      localStorage.setItem('pnp_onboarded_clients', JSON.stringify(localEntries));
    } catch { /* ignore */ }
    setClients(merged);
    if (!backendOk && localClients.filter(c => c._source === 'backend').length === 0 && localClients.filter(c => c._source === 'local').length === 0) {
      setError('Using demo data — backend not reachable');
    }
    setLoading(false);
  };

  const getDemoClients = () => {
    // Only clients with DB Ready product types (live databases configured)
    return [
      // ── Platform Operator ──
      { client_id: 'builder_master_001', company_name: 'Empirical-AiS Platform Engine', plan: 'enterprise', tenant_type: 'PARENT', tenant_role: 'platform_operator', status: 'active', location: 'EC2 Production (3.219.218.53)', product_name: 'Comm-AI-Core', product_type: 'Platform-Architect', db_config: { db: 'pnp_platform', host: 'localhost', port: '5432', engine: 'PostgreSQL', tables: 8 } },

      // ── Restaurant.Type-1 (PROD — restaurant-ops-v1) ──
      { client_id: 'client_prab_001', company_name: 'Prab-Restaurant-Hospitality', plan: 'starter', tenant_type: 'INDEPENDENT', tenant_role: 'subscriber', status: 'active', location: 'Charlotte, NC 28277', product_name: 'Restaurant Intelligence.Type-1', product_type: 'Restaurant.Type-1' },
      { client_id: 'client_metrospice_001', company_name: 'MetroSpice-Restaurant', plan: 'starter', tenant_type: 'SUB_CLIENT', tenant_role: 'subscriber', status: 'active', location: 'Kona Point, Unit A-101', product_name: 'Restaurant Intelligence.Type-1', product_type: 'Restaurant.Type-1' },

      // ── Gym.Type-1 (PROD — gym-ops-v1) ──
      { client_id: 'client_ironorbit_001', company_name: 'IronOrbit-Gym', plan: 'growth', tenant_type: 'SUB_CLIENT', tenant_role: 'subscriber', status: 'active', location: 'Kona Point, Unit B-201', product_name: 'Gym Intelligence.Type-1', product_type: 'Gym.Type-1' },
      { client_id: 'client_3a1821e2', company_name: 'Sports-Avatar-Athletics', plan: 'growth', tenant_type: 'INDEPENDENT', tenant_role: 'subscriber', status: 'active', location: 'Charlotte, NC 28277', product_name: 'Gym Intelligence.Type-1', product_type: 'Gym.Type-1' },

      // ── Financial-Bank-Type1 (PROD — fin-bank-qrm-light-v1) ──
      { client_id: 'client_pnb_fin_001', company_name: 'Prabs-National-Bank', plan: 'enterprise', tenant_type: 'INDEPENDENT', tenant_role: 'subscriber', status: 'active', location: 'Charlotte, NC 28277', product_name: 'BankRisk Intelligence.Type-1', product_type: 'Financial-Bank-Type1' },

      // ── Grocery.Type-1 (PROD — supermarket-n-restaurant-v1) ──
      { client_id: 'client_freshbasket_001', company_name: 'FreshBasket-Grocery', plan: 'growth', tenant_type: 'SUB_CLIENT', tenant_role: 'subscriber', status: 'active', location: 'Kona Point, Unit A-105', product_name: 'Grocery Intelligence.Type-1', product_type: 'Grocery.Type-1' },
    ];
  };

  const filtered = clients.filter(c => {
    const term = searchTerm.toLowerCase();
    return (
      c.company_name?.toLowerCase().includes(term) ||
      c.client_id?.toLowerCase().includes(term) ||
      c.tenant_type?.toLowerCase().includes(term) ||
      c.tenant_role?.toLowerCase().includes(term) ||
      c.product_name?.toLowerCase().includes(term) ||
      c.product_type?.toLowerCase().includes(term) ||
      c.plan?.toLowerCase().includes(term) ||
      c.location?.toLowerCase().includes(term) ||
      c.app_model?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const planBadge = (plan) => {
    const colors = { starter: '#4CAF50', growth: '#2196F3', enterprise: '#9C27B0' };
    return (
      <span style={{
        background: colors[plan] || '#666', color: '#fff', padding: '2px 8px',
        borderRadius: '12px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase'
      }}>{plan}</span>
    );
  };

  const typeBadge = (type) => {
    const colors = { PARENT: '#FF9800', SUB_CLIENT: '#03A9F4', INDEPENDENT: '#4CAF50' };
    return (
      <span style={{
        background: colors[type] || '#666', color: '#fff', padding: '2px 8px',
        borderRadius: '12px', fontSize: '11px', fontWeight: 600
      }}>{type}</span>
    );
  };

  const roleBadge = (role) => {
    const colors = { builder: '#7B1FA2', subscriber: '#1565C0', distributor: '#FF6F00', platform_operator: '#C62828' };
    const labels = { builder: 'Builder', subscriber: 'Subscriber', distributor: 'Distributor', platform_operator: 'Platform' };
    return (
      <span style={{
        background: colors[role] || '#666', color: '#fff', padding: '2px 8px',
        borderRadius: '12px', fontSize: '11px', fontWeight: 600
      }}>{labels[role] || role || 'subscriber'}</span>
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '4px' }}>📋 Client Directory</h2>
      <p style={{ color: '#666', marginBottom: '12px' }}>All registered clients on Plug-N-Play-Infra.ai</p>

      {/* View Mode Toggle: Clients vs Products */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => { setViewMode('clients'); setCurrentPage(1); }}
          style={{
            padding: '8px 16px', border: '1px solid #ddd', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            background: viewMode === 'clients' ? '#1a1a2e' : '#fff',
            color: viewMode === 'clients' ? '#fff' : '#333',
          }}
        >
          👥 Clients ({clients.length})
        </button>
        <button
          onClick={() => { setViewMode('products'); setCurrentPage(1); }}
          style={{
            padding: '8px 16px', border: '1px solid #ddd', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            background: viewMode === 'products' ? '#1a1a2e' : '#fff',
            color: viewMode === 'products' ? '#fff' : '#333',
          }}
        >
          📦 Products by Builders ({products.length})
        </button>
      </div>

      <input
        type="text"
        placeholder={viewMode === 'clients' ? 'Search by name, client ID, or tenant type...' : 'Search products by name, type, or industry...'}
        value={searchTerm}
        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        style={{
          width: '100%', padding: '10px 14px', border: '1px solid #ddd',
          borderRadius: '8px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box'
        }}
        aria-label={viewMode === 'clients' ? 'Search clients' : 'Search products'}
      />

      {error && <p style={{ color: '#FF9800', fontSize: '13px', marginBottom: '12px' }}>⚠️ {error}</p>}

      {loading ? (
        <p>Loading...</p>
      ) : viewMode === 'products' ? (
        /* ── Products by Builders View ── */
        (() => {
          const filteredProducts = products.filter(p => {
            const term = searchTerm.toLowerCase();
            return (
              p.product_name?.toLowerCase().includes(term) ||
              p.product_type?.toLowerCase().includes(term) ||
              p.industry?.toLowerCase().includes(term) ||
              p.type_description?.toLowerCase().includes(term)
            );
          });
          const totalProdPages = Math.ceil(filteredProducts.length / PAGE_SIZE);
          const pagedProducts = filteredProducts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
          return (
            <>
              <p style={{ color: '#555', fontSize: '13px', marginBottom: '12px' }}>
                Products built by platform builders — subscribers customize these for their business.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                    <th style={{ padding: '10px 8px' }}>Product Name</th>
                    <th style={{ padding: '10px 8px' }}>Product Type</th>
                    <th style={{ padding: '10px 8px' }}>Industry</th>
                    <th style={{ padding: '10px 8px' }}>Builder</th>
                    <th style={{ padding: '10px 8px' }}>Subscribers</th>
                    <th style={{ padding: '10px 8px' }}>Version</th>
                    <th style={{ padding: '10px 8px' }}>Marketplace</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedProducts.map((p, i) => {
                    // Use backend product_type or fall back to local map
                    const ptStatus = getProductTypeDbStatus(p.product_type);
                    return (
                    <tr key={p.product_id || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 8px', fontWeight: 500 }}>{p.product_name}</td>
                      <td style={{ padding: '10px 8px', fontSize: '11px' }}>
                        <span title={ptStatus.tooltip} style={{ color: ptStatus.color, cursor: 'help', marginRight: '5px', fontSize: '12px' }}>💡</span>
                        <span style={{ color: '#1565C0' }}>{p.product_type}</span>
                      </td>
                      <td style={{ padding: '10px 8px', color: '#666' }}>{p.industry || '—'}</td>
                      <td style={{ padding: '10px 8px', fontSize: '12px' }}>
                        <span style={{ color: '#555' }}>{p.builder_name || 'Empirical-AiS'}</span>
                        {p.builder_role === 'platform_operator' && (
                          <span style={{ marginLeft: '4px', background: '#F3E5F5', color: '#7B1FA2', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600 }}>Platform</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        <span style={{ background: (p.subscriber_count || 0) > 0 ? '#E8F5E9' : '#f5f5f5', color: (p.subscriber_count || 0) > 0 ? '#2E7D32' : '#999', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                          {p.subscriber_count || 0}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>{p.version || 'v1'}</span>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ background: ptStatus.color === '#4CAF50' ? '#E8F5E9' : '#FFF3E0', color: ptStatus.color === '#4CAF50' ? '#2E7D32' : '#E65100', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                          {ptStatus.label === 'prod' ? '🟢 DB Ready' : '🟠 Demo Only'}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                  {pagedProducts.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No products found. Products are loaded from the PostgreSQL product catalog.</td></tr>
                  )}
                </tbody>
              </table>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                <p style={{ color: '#999', fontSize: '12px', margin: 0 }}>
                  Showing {filteredProducts.length > 0 ? ((currentPage - 1) * PAGE_SIZE) + 1 : 0}–{Math.min(currentPage * PAGE_SIZE, filteredProducts.length)} of {filteredProducts.length} products
                </p>
                {totalProdPages > 1 && (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === 1 ? '#f5f5f5' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', fontSize: '12px' }}>«</button>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === 1 ? '#f5f5f5' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', fontSize: '12px' }}>‹ Prev</button>
                    <span style={{ fontSize: '12px', color: '#555', padding: '0 8px' }}>Page {currentPage} of {totalProdPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalProdPages, p + 1))} disabled={currentPage === totalProdPages}
                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === totalProdPages ? '#f5f5f5' : '#fff', cursor: currentPage === totalProdPages ? 'default' : 'pointer', fontSize: '12px' }}>Next ›</button>
                    <button onClick={() => setCurrentPage(totalProdPages)} disabled={currentPage === totalProdPages}
                      style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === totalProdPages ? '#f5f5f5' : '#fff', cursor: currentPage === totalProdPages ? 'default' : 'pointer', fontSize: '12px' }}>»</button>
                  </div>
                )}
              </div>
            </>
          );
        })()
      ) : (
        /* ── Clients View (existing) ── */
        <>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
              <th style={{ padding: '10px 8px' }}>Company Name</th>
              <th style={{ padding: '10px 8px' }}>Client ID</th>
              <th style={{ padding: '10px 8px' }}>Plan</th>
              <th style={{ padding: '10px 8px' }}>Tenant Type</th>
              <th style={{ padding: '10px 8px' }}>Role</th>
              <th style={{ padding: '10px 8px' }}>Product Type</th>
              <th style={{ padding: '10px 8px' }}>Location</th>
              <th style={{ padding: '10px 8px' }}>Status</th>
              <th style={{ padding: '10px 8px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(c => {
              const dbStatus = getDbStatus(c.db_config, c.product_type);
              return (
              <tr key={c.client_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px 8px', fontWeight: 500 }}>
                  <span title={dbStatus.tooltip} style={{ color: dbStatus.color, cursor: 'help', marginRight: '6px', fontSize: '10px' }}>●</span>
                  {c.company_name}
                </td>
                <td style={{ padding: '10px 8px', fontFamily: 'monospace', fontSize: '12px' }}>{c.client_id}</td>
                <td style={{ padding: '10px 8px' }}>{planBadge(c.plan)}</td>
                <td style={{ padding: '10px 8px' }}>{typeBadge(c.tenant_type)}</td>
                <td style={{ padding: '10px 8px' }}>{roleBadge(c.tenant_role)}</td>
                <td style={{ padding: '10px 8px', fontSize: '11px', color: '#1565C0' }}>{c.product_type || '—'}</td>
                <td style={{ padding: '10px 8px', color: '#666' }}>{c.location}</td>
                <td style={{ padding: '10px 8px' }}>
                  <span style={{ color: c.status === 'active' ? '#4CAF50' : '#FF9800' }}>● {c.status}</span>
                </td>
                <td style={{ padding: '10px 8px' }}>
                  {c.status === 'active' && onLaunchApp && (
                    <button
                      onClick={() => onLaunchApp({
                        client_id: c.client_id,
                        company_name: c.company_name,
                        product_name: c.product_name || c.company_name,
                        tenant_type: c.tenant_type,
                        tenant_role: c.tenant_role,
                        plan: c.plan,
                        product_type: c.product_type,
                        db_config: resolveDbConfig(c.db_config, c.product_type),
                      })}
                      style={{
                        padding: '4px 12px', background: '#2E7D32', color: '#fff', border: 'none',
                        borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ▶ Launch
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <p style={{ color: '#999', fontSize: '12px', margin: 0 }}>
          Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} clients ({clients.length} total)
        </p>
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === 1 ? '#f5f5f5' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', fontSize: '12px' }}>«</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === 1 ? '#f5f5f5' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', fontSize: '12px' }}>‹ Prev</button>
            <span style={{ fontSize: '12px', color: '#555', padding: '0 8px' }}>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === totalPages ? '#f5f5f5' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', fontSize: '12px' }}>Next ›</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
              style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', background: currentPage === totalPages ? '#f5f5f5' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', fontSize: '12px' }}>»</button>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default ListClients;
